// @ts-nocheck
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { Input } from '@frontend/shadcn/components/ui/input';
import {
  ListCheckIcon, ChevronUpIcon, CircleInfoIcon,
  BoldIcon, ItalicIcon, UnderlineIcon,
  ListIcon, ListOlIcon, LinkIcon,
  GripVerticalIcon, PenIcon, PlusIcon,
  XmarkIcon, CheckIcon
} from '@revfluence/fresh-icons/regular/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import RichTextEditor from '@frontend/app/refresh-components/RichTextEditor';
import { useCustomStages } from '../context/CustomStagesContext';
import { FieldFormPopover } from './FieldFormPopover';
import { TaskSettingsSection } from './TaskSettingsSection';

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'multiSelect' | 'yesNo' | 'checkbox' | 'date' | 'file' | 'url' | 'callToAction';
  required: boolean;
  options?: string[];
  description?: string;
  campaignOnly?: boolean;
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
}

const RichTextEditorOld: React.FC<RichTextEditorProps> = ({ value, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false
  });

  // Initialize editor content once mounted
  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleFormat = (command: string) => {
    document.execCommand(command, false);

    if (editorRef.current) {
      setActiveFormats({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline')
      });

      onChange(editorRef.current.innerHTML);
    }
  };

  const handleLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      document.execCommand('createLink', false, url);
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
    }
  };

  const handleEditorChange = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);

      setActiveFormats({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline')
      });
    }
  };

  return (
    <div className="self-stretch bg-white rounded-xl border border-gray-200 flex flex-col">
      <div
        ref={editorRef}
        className="p-3 min-h-[100px] focus:outline-none text-sm text-gray-800"
        contentEditable
        onInput={handleEditorChange}
        onKeyDown={(e) => {
          if (e.key === 'b' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleFormat('bold');
          }
          if (e.key === 'i' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleFormat('italic');
          }
          if (e.key === 'u' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleFormat('underline');
          }
        }}
        dir="ltr" // Ensure left-to-right text direction
        style={{
          whiteSpace: 'pre-wrap', // Preserve whitespace and line breaks
          wordBreak: 'break-word' // Handle long words
        }}
      />
      <div className="border-t border-gray-200" />
      <div className="h-8 px-3 flex items-center gap-2">
        <div className="flex items-center divide-x divide-gray-200">
          <div className="flex">
            <button
              className={cn(
                'w-6 h-6 p-2 rounded-md hover:bg-gray-100',
                activeFormats.bold && 'bg-gray-100'
              )}
              onClick={() => handleFormat('bold')}
            >
              <BoldIcon className="w-3 h-3 text-gray-800" />
            </button>
            <button
              className={cn(
                'w-6 h-6 p-2 rounded-md hover:bg-gray-100',
                activeFormats.italic && 'bg-gray-100'
              )}
              onClick={() => handleFormat('italic')}
            >
              <ItalicIcon className="w-3 h-3 text-gray-800" />
            </button>
            <button
              className={cn(
                'w-6 h-6 p-2 rounded-md hover:bg-gray-100',
                activeFormats.underline && 'bg-gray-100'
              )}
              onClick={() => handleFormat('underline')}
            >
              <UnderlineIcon className="w-3 h-3 text-gray-800" />
            </button>
          </div>
          <div className="flex pl-2">
            <button
              className="w-6 h-6 p-2 rounded-md hover:bg-gray-100"
              onClick={() => handleFormat('insertUnorderedList')}
            >
              <ListIcon className="w-3 h-3 text-gray-800" />
            </button>
            <button
              className="w-6 h-6 p-2 rounded-md hover:bg-gray-100"
              onClick={() => handleFormat('insertOrderedList')}
            >
              <ListOlIcon className="w-3 h-3 text-gray-800" />
            </button>
          </div>
          <div className="flex pl-2">
            <button
              className="w-6 h-6 p-2 rounded-md hover:bg-gray-100"
              onClick={handleLink}
            >
              <LinkIcon className="w-3 h-3 text-gray-800" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface Props {
  tasks: Array<{
    name: string;
    ctaLabel: string;
    creatorAccess: boolean;
    fields: any[];
  }>;
  setTasks: (tasks: Array<{
    name: string;
    ctaLabel: string;
    creatorAccess: boolean;
    fields: any[];
  }>) => void;
  taskSettings?: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  };
  setTaskSettings: (settings: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  }) => void;
}

export const TaskSection: React.FC<Props> = ({ tasks, setTasks, taskSettings, setTaskSettings }) => {
  const { selectedStage, updateStage, isNewStage } = useCustomStages();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isCreatorAccess, setIsCreatorAccess] = useState(selectedStage?.tasks[0]?.creatorAccess || false);
  const [details, setDetails] = useState<string>(selectedStage?.tasks[0]?.name || 'Task Details');
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);

  // Load initial data or set defaults when stage changes
  useEffect(() => {
    if (!isNewStage && selectedStage?.tasks?.[0]) {
      console.log('TaskSection: useEffect: ', selectedStage);
      const existingTask = selectedStage?.tasks[0];
      setDetails(existingTask.name || '');
      setIsCreatorAccess(existingTask.creatorAccess);
      setFormFields(existingTask.fields || []);
      if (selectedStage.taskSettings) {
        setTaskSettings({
          initialStatuses: selectedStage.taskSettings.initialStatuses || [],
          finalStatus: selectedStage.taskSettings.finalStatus || null
        });
      }
    } else if (isNewStage) {
      // Reset to defaults for new stage
      setDetails('Task Details');
      setIsCreatorAccess(false);
      setFormFields([]);
      setTaskSettings({
        initialStatuses: [],
        finalStatus: null
      });
    }
  // }, [isNewStage, selectedStage]); // Only trigger when stage changes
  }, [isNewStage, selectedStage, setTaskSettings]); // Only trigger when stage changes

  // Update tasks state when form data changes, but prevent unnecessary updates
  useEffect(() => {
    if (!details) return; // Don't update if details not loaded yet

    const updatedTask = {
      name: details,
      ctaLabel: 'Submit',
      creatorAccess: isCreatorAccess,
      fields: formFields
    };

    // Only update if the task data has actually changed
    const currentTask = tasks[0];
    if (!currentTask ||
        currentTask.name !== updatedTask.name ||
        currentTask.creatorAccess !== updatedTask.creatorAccess ||
        JSON.stringify(currentTask.fields) !== JSON.stringify(updatedTask.fields)) {
      if (isNewStage) {
        setTasks([updatedTask]);
      } else if (selectedStage?.id) {
        setTasks([updatedTask]);
        // updateStage(selectedStage.id, {
        //   tasks: [updatedTask]
        // });
      }
    }
  // }, [details, isCreatorAccess, formFields]);
  }, [details, isCreatorAccess, formFields, isNewStage, selectedStage?.id, setTasks, tasks]);

  const handleNameChange = (name: string) => {
    const existingTask = selectedStage?.tasks[0];
    setDetails(name);
    const updatedTask = {
      name,
      ctaLabel: 'Submit',
      creatorAccess: existingTask?.creatorAccess || false,
      fields: existingTask?.fields || []
    };

    updateStage(selectedStage.id, {
      tasks: [updatedTask]
    });
  };

  const handleTaskChange = (isCreatorAccess: boolean) => {
    const existingTask = selectedStage?.tasks[0];
    setIsCreatorAccess(isCreatorAccess);
    const updatedTask = {
      name: existingTask?.name || '',
      ctaLabel: 'Submit',
      creatorAccess: isCreatorAccess,
      fields: existingTask?.fields || []
    };

    updateStage(selectedStage.id, {
      tasks: [updatedTask]
    });
  };

  // const handleFormFieldChange = (fieldId: string, fieldData: FormField) => {
  //   setFormFields((fields) => fields.map((f) => f.id === fieldId ? fieldData : f));
  // };
  const handleFormFieldChange = useCallback((fieldId: string, fieldData: FormField) => {
    setFormFields((fields) => fields.map((f) => (f.id === fieldId ? fieldData : f)));
  }, []);

  const handleAddField = () => {
    setShowFieldForm(true);
  };

  const handleEditField = (fieldId: string) => {
    setEditingFieldId(fieldId);
    setShowFieldForm(true);
  };

  const getInternalFieldType = (displayType: string): FormField['type'] => {
    switch (displayType) {
      case 'Free Text':
        return 'text';
      case 'Number':
        return 'number';
      case 'Single Select':
        return 'select';
      case 'Multi Select':
        return 'multiSelect';
      case 'Yes/No':
        return 'yesNo';
      case 'Checkbox':
        return 'checkbox';
      case 'Date':
        return 'date';
      case 'Upload File':
        return 'file';
      case 'URL':
        return 'url';
      case 'Call to Action':
        return 'callToAction';
      default:
        return 'text';
    }
  };

  // Update the stage context when form fields change
  const handleSaveField = (fieldData: {
    name: string;
    type: string;
    campaignOnly: boolean;
    options?: string[];
    description?: string;
  }) => {
    const updatedFields = editingFieldId
      ? formFields.map((field) =>
          (field.id === editingFieldId
            ? {
                ...field,
                label: fieldData.name,
                type: getInternalFieldType(fieldData.type),
                options: fieldData.options,
                description: fieldData.description,
                campaignOnly: fieldData.campaignOnly,
              }
            : field)
        )
      : [
          ...formFields,
          {
            id: String(Date.now()),
            label: fieldData.name,
            type: getInternalFieldType(fieldData.type),
            required: false,
            options: fieldData.options,
            description: fieldData.description,
            campaignOnly: fieldData.campaignOnly,
          }
        ];

    setFormFields(updatedFields);

    // Update the stage context
    if (selectedStage && selectedStage.tasks[0]) {
      updateStage(selectedStage.id, {
        tasks: [{
          ...selectedStage.tasks[0],
          fields: updatedFields,
          taskSettings,
        }]
      });
    }

    setShowFieldForm(false);
    setEditingFieldId(null);
  };

  const handleDeleteField = (fieldId: string) => {
    setFormFields((fields) => fields.filter((f) => f.id !== fieldId));
  };

  const getFieldTypeDisplay = (type: FormField['type']): string => {
    switch (type) {
      case 'text':
        return 'Free Text';
      case 'number':
        return 'Number';
      case 'select':
        return 'Single Select';
      case 'multiSelect':
        return 'Multi Select';
      case 'yesNo':
        return 'Yes/No';
      case 'checkbox':
        return 'Checkbox';
      case 'date':
        return 'Date';
      case 'file':
        return 'Upload File';
      case 'url':
        return 'URL';
      case 'callToAction':
        return 'Call to Action';
      default:
        return type;
    }
  };

  const getEditingField = () => {
    if (!editingFieldId) return null;
    const field = formFields.find((f) => f.id === editingFieldId);
    if (!field) return null;

    return {
      name: field.label,
      type: getFieldTypeDisplay(field.type),
      campaignOnly: field.campaignOnly || false,
      description: field.description,
      options: field.options,
    };
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(formFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFormFields(items);
  };

  const handleTaskSettingsChange = (settings: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  }) => {
    setTaskSettings(settings);
    updateStage(selectedStage.id, {
      taskSettings: settings
    });
  };

  return (
    <>
      <div className="p-6 bg-white rounded-2xl border border-gray-200 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-orange-400 rounded-lg flex items-center justify-center">
              <ListCheckIcon className="w-3 h-3 text-white" />
            </div>
            <span className="flex-1 text-base font-medium text-gray-800">Task</span>

            <div className="flex items-center gap-2">
              <Switch
                checked={isCreatorAccess}
                onCheckedChange={handleTaskChange}
                className="bg-gray-800"
              />
              <span className="text-sm text-gray-800">Creator Access</span>
              <CircleInfoIcon className="w-3 h-3 text-gray-300" />
            </div>

            <button
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <ChevronUpIcon className={cn('w-3.5 h-3.5 text-gray-800 transition-transform', !isExpanded && 'rotate-180')} />
            </button>
          </div>
          <p className="text-sm text-gray-500">
            Outline all necessary details and form fields. This task may be completed by creators and made visible in the Aspire Creator Portal.
          </p>
        </div>

        {isExpanded && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-gray-800">Details</span>
                <CircleInfoIcon className="w-3 h-3 text-gray-300" />
              </div>
              <RichTextEditor
                // key={`task-details-${selectedStage?.name}-${selectedStage?.tasks?.[0]?.name}`}
                value={details}
                // onChange={setDetails}
                onChange={handleNameChange}
                className="[&_.ql-editor]:min-h-[100px]"
                initialValue={selectedStage?.tasks?.[0]?.name || 'Task Details'}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800">Form Fields</span>
                <button
                  className="h-6 flex items-center gap-2 text-sm font-medium text-gray-800"
                  onClick={handleAddField}
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add Field
                </button>
              </div>

              {formFields.length === 0 ? (
                <div data-layout="Default" data-size="Small" data-type="Empty" className="self-stretch inline-flex justify-center items-center">
                  <div data-type="Small - Filled" className="w-80 p-2 bg-white flex justify-center items-start gap-2.5">
                    <div className="flex-1 text-center justify-start text-gray-500 text-sm font-normal font-['Inter'] leading-normal">
                      Add a form field.
                    </div>
                  </div>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="form-fields">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="flex flex-col gap-2"
                      >
                        {formFields.map((field, index) => (
                          <Draggable
                            key={field.id}
                            draggableId={field.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={cn(
                                  'flex items-center gap-2 p-2 border border-gray-200 rounded-xl',
                                  snapshot.isDragging && 'bg-gray-50'
                                )}
                              >
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab"
                                >
                                  <GripVerticalIcon className="w-3 h-3 text-gray-300" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-1">
                                    <span className="text-sm font-medium text-gray-800">
                                      {field.label}
                                    </span>
                                    {field.required && (
                                      <span className="text-sm text-red-500">*</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                                    onClick={() => handleEditField(field.id)}
                                  >
                                    <PenIcon className="w-3.5 h-3.5 text-gray-800" />
                                  </button>
                                  <button
                                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                                    onClick={() => handleDeleteField(field.id)}
                                  >
                                    <XmarkIcon className="w-3.5 h-3.5 text-red-400" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </div>
        )}
      </div>

      <TaskSettingsSection
        creatorAccess={isCreatorAccess}
        statuses={selectedStage?.statuses || []}
        selectedTaskStatuses={taskSettings?.initialStatuses || []}
        selectedResultingStatus={taskSettings?.finalStatus || null}
        onTaskSettingsChange={handleTaskSettingsChange}
      />

      {showFieldForm && (
        <FieldFormPopover
          onClose={() => {
            setShowFieldForm(false);
            setEditingFieldId(null);
          }}
          onSave={handleSaveField}
          initialData={editingFieldId ? getEditingField() || undefined : undefined}
        />
      )}
    </>
  );
};
